import { DragDropModule } from '@angular/cdk/drag-drop';
import { HttpClientModule } from '@angular/common/http';
import { NgModule, ErrorHandler } from '@angular/core';
import { FormsModule } from '@angular/forms';
import { BrowserAnimationsModule } from '@angular/platform-browser/animations';

import * as Sentry from '@sentry/angular';

import { NgbDatepickerModule, NgbDropdownModule, NgbModal, NgbModalModule, NgbTooltipModule, NgbTypeaheadModule } from '@ng-bootstrap/ng-bootstrap';

// FullCalendar setup
import { FullCalendarModule } from '@fullcalendar/angular';
import dayGridPlugin from '@fullcalendar/daygrid';
import timeGridPlugin from '@fullcalendar/timegrid';
import listPlugin from '@fullcalendar/list';
import scrollGridPlugin from '@fullcalendar/scrollgrid';
import bootstrapPlugin from '@fullcalendar/bootstrap';


import { StaffSiteRoutingModule } from './staff-site-routing.module';
import { StaffSiteComponent } from './staff-site.component';
import { StackingNgbModal } from './stacking-ngb-modal.service';

import { NavigationSideComponent } from './components/navigation-side/navigation-side.component';
import { NavigationTopComponent } from './components/navigation-top/navigation-top.component';
import { AccountSettingsComponent } from './components/account-settings/account-settings.component';
import { CustomerDetailComponent } from './components/customers/customer-detail/customer-detail.component';
import { UserListComponent } from './components/admin/user-list/user-list.component';
import { UserComponent } from './components/admin/user-list/user/user.component';
import { EventsComponent } from './components/events/events.component';
import { EventsListComponent } from './components/events/scheduled-events/events-list/events-list.component';
import { EventTypesListComponent } from './components/events/event-setup/event-types-list/event-types-list.component';
import { ScheduledEventsComponent } from './components/events/scheduled-events/scheduled-events.component';
import { EventSetupComponent } from './components/events/event-setup/event-setup.component';
import { EventTypeEditComponent } from './components/events/event-setup/event-type-edit/event-type-edit.component';
import { EventTypeSchedulesComponent } from './components/events/event-setup/event-type-schedules/event-type-schedules.component';
import { EventTypeQuestionsComponent } from './components/events/event-setup/event-type-questions/event-type-questions.component';
import { EventSettingsComponent } from './components/events/event-settings/event-settings.component';
import {
  EventTypeCancellationPolicyComponent
} from './components/events/event-setup/event-type-cancellation-policy/event-type-cancellation-policy.component';
import {
  CreditsAndVouchersSubListComponent
} from './components/customers/customer-detail/sub-lists/credits-and-vouchers-sub-list/credits-and-vouchers-sub-list.component';
import {
  MembershipsAndPassesSubListComponent
} from './components/customers/customer-detail/sub-lists/memberships-and-passes-sub-list/memberships-and-passes-sub-list.component';
import {
  CheckInsSubListComponent
} from './components/customers/customer-detail/sub-lists/check-ins-sub-list/check-ins-sub-list.component';
import {
  CheckInAlertsModalComponent
} from './components/customers/customer-detail/sub-lists/check-ins-sub-list/modals/check-in-alerts-modal/check-in-alerts-modal.component';
import {
  CheckInNoEntryMethodModalComponent
// tslint:disable-next-line:max-line-length
} from './components/customers/customer-detail/sub-lists/check-ins-sub-list/modals/check-in-no-entry-method-modal/check-in-no-entry-method-modal.component';
import { BulkAddCreditOrVoucherComponent } from './components/customers/bulk-add-credit-or-voucher/bulk-add-credit-or-voucher.component';
import { DocumentTemplatesComponent } from './components/admin/document-templates/document-templates.component';
import { DocumentTemplateEditComponent } from './components/admin/document-templates/document-template-edit/document-template-edit.component';
import {
  DocumentTemplateElementComponent
// tslint:disable-next-line:max-line-length
} from './components/admin/document-templates/document-template-edit/document-template-edit-sections/document-template-element/document-template-element.component';
import {
  DocumentTemplateEditSectionsComponent
} from './components/admin/document-templates/document-template-edit/document-template-edit-sections/document-template-edit-sections.component';
import {
  DocumentTemplateEditSettingsComponent
} from './components/admin/document-templates/document-template-edit/document-template-edit-settings/document-template-edit-settings.component';
import {
  DocumentTemplateEditLinkedProductsComponent
// tslint:disable-next-line:max-line-length
} from './components/admin/document-templates/document-template-edit/document-template-edit-linked-products/document-template-edit-linked-products.component';
import { DocumentTemplateAddComponent } from './components/admin/document-templates/document-template-add/document-template-add.component';
import { DocumentPoliciesComponent } from './components/admin/document-templates/document-policies/document-policies.component';
import { DocumentTemplatesNavComponent } from './components/admin/document-templates/document-templates-nav/document-templates-nav.component';
import {
  DocumentPolicyFormComponent
} from './components/admin/document-templates/document-policies/document-policy-form/document-policy-form.component';
import {
  DocumentPolicyAddComponent
} from './components/admin/document-templates/document-policies/document-policy-add/document-policy-add.component';
import {
  DocumentPolicyEditComponent
} from './components/admin/document-templates/document-policies/document-policy-edit/document-policy-edit.component';
import {
  CustomerProfileLayoutComponent
} from './components/admin/customer-profile-config/customer-profile-layout/customer-profile-layout.component';
import { EditCustomerProfileComponent } from './components/customers/customer-detail/edit-customer-profile/edit-customer-profile.component';
import {
  AddLinkedAccountModalComponent
} from './components/customers/customer-detail/modals/add-linked-account-modal/add-linked-account-modal.component';
import { LinkingAccountsConfigComponent } from './components/admin/linking-accounts-config/linking-accounts-config.component';
import {
  ReviewPendingLinkedAccountModalComponent
} from './components/customers/customer-detail/modals/review-pending-linked-account-modal/review-pending-linked-account-modal.component';
import { InboxComponent } from './components/inbox/inbox.component';
import {
  InboxLinkingAccountRequestsComponent
} from './components/inbox/inbox-customers/inbox-linking-account-requests/inbox-linking-account-requests.component';
import { InboxCustomersComponent } from './components/inbox/inbox-customers/inbox-customers.component';
import {
  ConfirmRemoveLinkedAccountModalComponent
} from './components/customers/customer-detail/modals/confirm-remove-linked-account-modal/confirm-remove-linked-account-modal.component';
import { TaxRatesConfigComponent } from './components/admin/tax-rates-config/tax-rates-config.component';
import { AddTaxRateModalComponent } from './components/admin/tax-rates-config/modals/add-tax-rate-modal/add-tax-rate-modal.component';
import { EditTaxRateModalComponent } from './components/admin/tax-rates-config/modals/edit-tax-rate-modal/edit-tax-rate-modal.component';
import { RemoveTaxRateModalComponent } from './components/admin/tax-rates-config/modals/remove-tax-rate-modal/remove-tax-rate-modal.component';
import { MembershipsComponent } from './components/admin/memberships/memberships.component';
import { MembershipDetailComponent } from './components/admin/memberships/membership-detail/membership-detail.component';
import { DashboardComponent } from './components/dashboard/dashboard.component';
import { AddStaffNoteModalComponent } from './components/dashboard/modals/add-staff-note-modal/add-staff-note-modal.component';
import { EditStaffNoteModalComponent } from './components/dashboard/modals/edit-staff-note-modal/edit-staff-note-modal.component';
import { EntryPassDetailComponent } from './components/admin/memberships/entry-pass-detail/entry-pass-detail.component';
import { CheckInComponent } from './components/check-in/check-in.component';
import { OrganizationSettingsComponent } from './components/admin/organization-settings/organization-settings.component';
import { PaymentsSubListComponent } from './components/customers/customer-detail/sub-lists/payments-sub-list/payments-sub-list.component';
import { PurchasesSubListComponent } from './components/customers/customer-detail/sub-lists/purchases-sub-list/purchases-sub-list.component';
import { PaymentsComponent } from './components/payments/payments.component';
import { ProficienciesConfigComponent } from './components/admin/proficiencies-config/proficiencies-config.component';
import {
  AddProficiencyModalComponent
} from './components/admin/proficiencies-config/modals/add-proficiency-modal/add-proficiency-modal.component';
import {
  EditProficiencyModalComponent
} from './components/admin/proficiencies-config/modals/edit-proficiency-modal/edit-proficiency-modal.component';
import {
  RemoveProficiencyModalComponent
} from './components/admin/proficiencies-config/modals/remove-proficiency-modal/remove-proficiency-modal.component';
import {
  AddEditCustomerProficiencyModalComponent
} from './components/customers/customer-detail/modals/add-edit-customer-proficiency-modal/add-edit-customer-proficiency-modal.component';
import { CapitanCommonModule } from '../../capitan-common.module';
import { CheckInSoundsConfigComponent } from './components/admin/check-in-sounds-config/check-in-sounds-config.component';
import {
  ChooseCheckInSharedPassModalComponent
// tslint:disable-next-line:max-line-length
} from './components/customers/customer-detail/sub-lists/check-ins-sub-list/modals/choose-check-in-shared-pass-modal/choose-check-in-shared-pass-modal.component';
import {
  EditRecurringCustomerMembershipModalComponent
// tslint:disable-next-line:max-line-length
} from './components/customers/customer-detail/sub-lists/memberships-and-passes-sub-list/modals/edit-recurring-customer-membership-modal/edit-recurring-customer-membership-modal.component';
import {
  MembershipDetailsModalComponent
// tslint:disable-next-line:max-line-length
} from './components/customers/customer-detail/sub-lists/memberships-and-passes-sub-list/modals/membership-details-modal/membership-details-modal.component';
import {
  EditRecurringCustomerMembershipCancelImmediatelyComponent
// tslint:disable-next-line:max-line-length
} from './components/customers/customer-detail/sub-lists/memberships-and-passes-sub-list/modals/edit-recurring-customer-membership-modal/edit-recurring-customer-membership-cancel-immediately/edit-recurring-customer-membership-cancel-immediately.component';
import {
  EditRecurringCustomerMembershipCancelFutureBillDateComponent
// tslint:disable-next-line:max-line-length
} from './components/customers/customer-detail/sub-lists/memberships-and-passes-sub-list/modals/edit-recurring-customer-membership-modal/edit-recurring-customer-membership-cancel-future-bill-date/edit-recurring-customer-membership-cancel-future-bill-date.component';
import {
  EditRecurringCustomerMembershipUndoCancelFutureBillDateComponent
// tslint:disable-next-line:max-line-length
} from './components/customers/customer-detail/sub-lists/memberships-and-passes-sub-list/modals/edit-recurring-customer-membership-modal/edit-recurring-customer-membership-undo-cancel-future-bill-date/edit-recurring-customer-membership-undo-cancel-future-bill-date.component';
import {
  EditRecurringCustomerMembershipFreezeComponent
// tslint:disable-next-line:max-line-length
} from './components/customers/customer-detail/sub-lists/memberships-and-passes-sub-list/modals/edit-recurring-customer-membership-modal/edit-recurring-customer-membership-freeze/edit-recurring-customer-membership-freeze.component';
import {
  EditRecurringCustomerMembershipUndoScheduledFreezeComponent
// tslint:disable-next-line:max-line-length
} from './components/customers/customer-detail/sub-lists/memberships-and-passes-sub-list/modals/edit-recurring-customer-membership-modal/edit-recurring-customer-membership-undo-scheduled-freeze/edit-recurring-customer-membership-undo-scheduled-freeze.component';
import {
  EditRecurringCustomerMembershipUnfreezeComponent
// tslint:disable-next-line:max-line-length
} from './components/customers/customer-detail/sub-lists/memberships-and-passes-sub-list/modals/edit-recurring-customer-membership-modal/edit-recurring-customer-membership-unfreeze/edit-recurring-customer-membership-unfreeze.component';
import {
  EditNonRecurringCustomerMembershipModalComponent
// tslint:disable-next-line:max-line-length
} from './components/customers/customer-detail/sub-lists/memberships-and-passes-sub-list/modals/edit-non-recurring-customer-membership-modal/edit-non-recurring-customer-membership-modal.component';
import {
  EditNonRecurringCustomerMembershipCancelComponent
// tslint:disable-next-line:max-line-length
} from './components/customers/customer-detail/sub-lists/memberships-and-passes-sub-list/modals/edit-non-recurring-customer-membership-modal/edit-non-recurring-customer-membership-cancel/edit-non-recurring-customer-membership-cancel.component';
import {
  EditPrepaidCustomerMembershipUnfreezeComponent
// tslint:disable-next-line:max-line-length
} from './components/customers/customer-detail/sub-lists/memberships-and-passes-sub-list/modals/edit-non-recurring-customer-membership-modal/edit-prepaid-customer-membership-unfreeze/edit-prepaid-customer-membership-unfreeze.component';
import {
  EditCustomerEntryPassModalComponent
// tslint:disable-next-line:max-line-length
} from './components/customers/customer-detail/sub-lists/memberships-and-passes-sub-list/modals/edit-customer-entry-pass-modal/edit-customer-entry-pass-modal.component';
import {
  CreateCustomerEntryPassModalComponent
// tslint:disable-next-line:max-line-length
} from './components/customers/customer-detail/sub-lists/memberships-and-passes-sub-list/modals/create-customer-entry-pass-modal/create-customer-entry-pass-modal.component';
import {
  ReservationsSubListComponent
} from './components/customers/customer-detail/sub-lists/reservations-sub-list/reservations-sub-list.component';
// tslint:disable-next-line:max-line-length
import {
  UpcomingReservationsSubListComponent
// tslint:disable-next-line:max-line-length
} from './components/customers/customer-detail/sub-lists/upcoming-reservations-sub-list/upcoming-reservations-sub-list.component';
import {
  CustomerBookingsSubListComponent
// tslint:disable-next-line:max-line-length
} from './components/customers/customer-detail/sub-lists/customer-bookings-sub-list/customer-bookings-sub-list.component';
import {
  ReviewUnlinkedReservationModalComponent
// tslint:disable-next-line:max-line-length
} from './components/inbox/inbox-events/inbox-unlinked-reservations/modals/review-unlinked-reservation-modal/review-unlinked-reservation-modal.component';
import { MergeCustomersComponent } from './components/customers/merge-customers/merge-customers.component';
import {
  EventTypeSchedulesRepeatingComponent
} from './components/events/event-setup/event-type-schedules/event-type-schedules-repeating/event-type-schedules-repeating.component';
import {
  EventTypeSchedulesNonRepeatingComponent
// tslint:disable-next-line:max-line-length
} from './components/events/event-setup/event-type-schedules/event-type-schedules-non-repeating/event-type-schedules-non-repeating.component';
import { CancelEventModalComponent } from './components/events/modals/cancel-event-modal/cancel-event-modal.component';
import {
  ScheduleOrphanedEventsModalComponent
// tslint:disable-next-line:max-line-length
} from './components/events/modals/schedule-orphaned-events-modal/schedule-orphaned-events-modal.component';
import { LocationAreasConfigComponent } from './components/admin/location-areas-config/location-areas-config.component';
import { AddLocationAreaModalComponent } from './components/admin/location-areas-config/modals/add-gym-area-modal/add-location-area-modal.component';
import {
  EditLocationAreaModalComponent
} from './components/admin/location-areas-config/modals/edit-gym-area-modal/edit-location-area-modal.component';
import {
  RemoveLocationAreaModalComponent
} from './components/admin/location-areas-config/modals/remove-gym-area-modal/remove-location-area-modal.component';
import {
  CustomerNoteModalComponent
} from './components/customers/customer-detail/modals/customer-note-modal/customer-note-modal.component';
import {
  AddCustomerNoteModalComponent
} from './components/customers/customer-detail/modals/add-customer-note-modal/add-customer-note-modal.component';
import {
  AddCustomerAlertModalComponent
} from './components/customers/customer-detail/modals/add-customer-alert-modal/add-customer-alert-modal.component';
import {
  InboxInvalidDocumentsComponent
} from './components/inbox/inbox-customers/inbox-invalid-documents/inbox-invalid-documents.component';
import {
  ReviewInvalidDocumentModalComponent
// tslint:disable-next-line:max-line-length
} from './components/inbox/inbox-customers/inbox-invalid-documents/modals/review-invalid-document-modal/review-invalid-document-modal.component';
import {
  ViewDismissedInvalidDocumentModalComponent
// tslint:disable-next-line:max-line-length
} from './components/inbox/inbox-customers/inbox-invalid-documents/modals/view-dismissed-invalid-document-modal/view-dismissed-invalid-document-modal.component';
import { EventCalendarComponent } from './components/events/scheduled-events/event-calendar/event-calendar.component';
import {
  EditCustomerMembershipStartDateComponent
// tslint:disable-next-line:max-line-length
} from './components/customers/customer-detail/sub-lists/memberships-and-passes-sub-list/modals/edit-customer-membership-start-date/edit-customer-membership-start-date.component';
import {
  EditCustomerMembershipBillDateComponent
// tslint:disable-next-line:max-line-length
} from './components/customers/customer-detail/sub-lists/memberships-and-passes-sub-list/modals/edit-customer-membership-bill-date/edit-customer-membership-bill-date.component';
import {
  CancelUnactivatedCustomerMembershipComponent
// tslint:disable-next-line:max-line-length
} from './components/customers/customer-detail/sub-lists/memberships-and-passes-sub-list/modals/cancel-unactivated-customer-membership/cancel-unactivated-customer-membership.component';
import { CustomerFiltersModalComponent } from './components/customers/modals/customer-filters-modal/customer-filters-modal.component';
import {
  ManageBookingCancellationAndRefundComponent
} from './components/events/scheduled-events/booking-cancel/manage-booking-cancellation-and-refund.component';
import { CreateBookingModalComponent } from './components/events/modals/create-booking-modal/create-booking-modal.component';
import { AddParticipantToPrivateEventModalComponent } from './components/events/modals/add-participant-to-private-event-modal/add-participant-to-private-event-modal.component';
import {
  EventTypePricingMatricesComponent
} from './components/events/event-setup/event-type-pricing-matrices/event-type-pricing-matrices.component';
import { EventSetupNavComponent } from './components/events/event-setup/event-setup-nav/event-setup-nav.component';
import {
  EventTypePricingMatrixEditComponent
// tslint:disable-next-line:max-line-length
} from './components/events/event-setup/event-type-pricing-matrices/event-type-pricing-matrix-edit/event-type-pricing-matrix-edit.component';
import {
  RemoveEventTypePricingMatrixModalComponent
// tslint:disable-next-line:max-line-length
} from './components/events/event-setup/event-type-pricing-matrices/modals/remove-event-type-pricing-matrix-modal/remove-event-type-pricing-matrix-modal.component';
import {
  EditEventTypePricingMatrixFeeModalComponent
// tslint:disable-next-line:max-line-length
} from './components/events/event-setup/event-type-pricing-matrices/event-type-pricing-matrix-edit/modals/edit-event-type-pricing-matrix-fee-modal/edit-event-type-pricing-matrix-fee-modal.component';
import {
  InboxDocumentsPendingStaffApprovalComponent
} from './components/inbox/inbox-customers/inbox-documents-pending-staff-approval/inbox-documents-pending-staff-approval.component';
import {
  ReviewDocumentPendingStaffApprovalModalComponent
// tslint:disable-next-line:max-line-length
} from './components/inbox/inbox-customers/inbox-documents-pending-staff-approval/modals/review-document-pending-staff-approval-modal/review-document-pending-staff-approval-modal.component';
import {
  ViewDismissedDocumentPendingStaffApprovalModalComponent
// tslint:disable-next-line:max-line-length
} from './components/inbox/inbox-customers/inbox-documents-pending-staff-approval/modals/view-dismissed-document-pending-staff-approval-modal/view-dismissed-document-pending-staff-approval-modal.component';
import {
  EditPrepaidCustomerMembershipFreezeImmediatelyComponent
// tslint:disable-next-line:max-line-length
} from './components/customers/customer-detail/sub-lists/memberships-and-passes-sub-list/modals/edit-non-recurring-customer-membership-modal/edit-prepaid-customer-membership-freeze-immediately/edit-prepaid-customer-membership-freeze-immediately.component';
import { ConfigureAccountingConnectionComponent } from './components/admin/accounting-connections/configure-accounting-connection/configure-accounting-connection.component';
import {
  EditMembershipPerCustomerAddOnModalComponent
// tslint:disable-next-line:max-line-length
} from './components/admin/memberships/membership-detail/modals/edit-membership-per-customer-add-on-modal/edit-membership-per-customer-add-on-modal.component';
import {
  MembershipPricingSectionComponent
} from './components/admin/memberships/membership-detail/membership-pricing-section/membership-pricing-section.component';
import {
  EditCustomerMembershipAddOnsImmediatelyComponent
// tslint:disable-next-line:max-line-length
} from './components/customers/customer-detail/sub-lists/memberships-and-passes-sub-list/modals/edit-customer-membership-add-ons-immediately/edit-customer-membership-add-ons-immediately.component';
import { PaymentFiltersModalComponent } from './components/payments/modals/payment-filters-modal/payment-filters-modal.component';
import {
  LinkCustomerEntryPassModalComponent
// tslint:disable-next-line:max-line-length
} from './components/customers/customer-detail/sub-lists/memberships-and-passes-sub-list/modals/link-customer-entry-pass-modal/link-customer-entry-pass-modal.component';
import {
  PurchaseCustomerEntryPassModalComponent
// tslint:disable-next-line:max-line-length
} from './components/customers/customer-detail/sub-lists/memberships-and-passes-sub-list/modals/purchase-customer-entry-pass-modal/purchase-customer-entry-pass-modal.component';
import {
  PurchaseCustomerMembershipModalComponent
// tslint:disable-next-line:max-line-length
} from './components/customers/customer-detail/sub-lists/memberships-and-passes-sub-list/modals/purchase-customer-membership-modal/purchase-customer-membership-modal.component';
import {
  ChooseCheckInGuestPassModalComponent
// tslint:disable-next-line:max-line-length
} from './components/customers/customer-detail/sub-lists/check-ins-sub-list/modals/choose-check-in-guest-pass-modal/choose-check-in-guest-pass-modal.component';
import {
  EventTypeEditScheduleComponent
} from './components/events/event-setup/event-type-schedules/event-type-edit-schedule/event-type-edit-schedule.component';
import {
  EventTypeEditScheduleRepeatingComponent
// tslint:disable-next-line:max-line-length
} from './components/events/event-setup/event-type-schedules/event-type-edit-schedule/event-type-edit-schedule-repeating/event-type-edit-schedule-repeating.component';
import {
  EventTypeScheduleExceptionsComponent
} from './components/events/event-setup/event-type-schedule-exceptions/event-type-schedule-exceptions.component';
import {
  EventTypeEditScheduleExceptionComponent
// tslint:disable-next-line:max-line-length
} from './components/events/event-setup/event-type-schedule-exceptions/event-type-edit-schedule-exception/event-type-edit-schedule-exception.component';
import {
  EventTypeEditScheduleNonRepeatingComponent
// tslint:disable-next-line:max-line-length
} from './components/events/event-setup/event-type-schedules/event-type-edit-schedule/event-type-edit-schedule-non-repeating/event-type-edit-schedule-non-repeating.component';
import { TimeTrackingConfigComponent } from './components/admin/time-tracking-config/time-tracking-config.component';
import {
  AddTimeTrackingRoleModalComponent
} from './components/admin/time-tracking-config/modals/add-time-tracking-role-modal/add-time-tracking-role-modal.component';
import {
  EditTimeTrackingRoleModalComponent
} from './components/admin/time-tracking-config/modals/edit-time-tracking-role-modal/edit-time-tracking-role-modal.component';
import {
  RemoveTimeTrackingRoleModalComponent
} from './components/admin/time-tracking-config/modals/remove-time-tracking-role-modal/remove-time-tracking-role-modal.component';
import { ClockInOutModalComponent } from './components/modals/clock-in-out-modal/clock-in-out-modal.component';
import {
  SetQuickbooksDepositAccountModalComponent
} from './components/admin/accounting-connections/configure-accounting-connection/modals/set-quickbooks-deposit-account-modal/set-quickbooks-deposit-account-modal.component';
import { RebookModalComponent } from './components/events/modals/rebook-modal/rebook-modal.component';
import { ParticipantDetailsModalComponent } from './components/events/modals/participant-details-modal/participant-details-modal.component';
import { BookingDetailsModalComponent } from './components/events/modals/booking-details-modal/booking-details-modal.component';
import {
  CustomerEmergencyContactModalComponent
} from './components/customers/customer-detail/modals/customer-emergency-contact-modal/customer-emergency-contact-modal.component';
import {
  ExportEventAttendeesModalComponent
} from './components/events/modals/export-event-attendees-modal/export-event-attendees-modal.component';
import {
  CustomerActivityLogSubListComponent
} from './components/customers/customer-detail/sub-lists/activity-log-sub-list/customer-activity-log-sub-list.component';
import {
  EditActivityLogEntryNotesModalComponent
// tslint:disable-next-line:max-line-length
} from './components/customers/customer-detail/sub-lists/activity-log-sub-list/modals/edit-activity-log-entry-notes-modal/edit-activity-log-entry-notes-modal.component';
import {
  CustomerEmailsSubListComponent
} from './components/customers/customer-detail/sub-lists/emails-sub-list/customer-emails-sub-list.component';
import {
  BackendEmailDetailModalComponent
// tslint:disable-next-line:max-line-length
} from './components/customers/customer-detail/sub-lists/emails-sub-list/modals/backend-email-detail-modal/backend-email-detail-modal.component';
import {
  CheckInDetailModalComponent
} from './components/customers/customer-detail/sub-lists/check-ins-sub-list/modals/check-in-detail-modal/check-in-detail-modal.component';
import {
  ReassignCustomerMembershipComponent
// tslint:disable-next-line:max-line-length
} from './components/customers/customer-detail/sub-lists/memberships-and-passes-sub-list/modals/reassign-customer-membership/reassign-customer-membership.component';
import {
  CreateNewCustomerModalComponent
} from './components/customers/modals/create-new-customer-modal/create-new-customer-modal.component';
import {
  PrivateEventEditEventComponent
} from './components/events/scheduled-events/private-events/private-event-edit-event/private-event-edit-event.component';
import {
  PrivateEventSetupNavComponent
} from './components/events/scheduled-events/private-events/private-event-setup-nav/private-event-setup-nav.component';
import {
  PrivateEventEditReservationsComponent
} from './components/events/scheduled-events/private-events/private-event-edit-reservations/private-event-edit-reservations.component';
import { SelectCustomerModalComponent } from './components/modals/select-customer-modal/select-customer-modal.component';
import {
  PrivateEventEditPaymentComponent
} from './components/events/scheduled-events/private-events/private-event-edit-payment/private-event-edit-payment.component';
import {
  EventTypePricingMatrixDetailsComponent
// tslint:disable-next-line:max-line-length
} from './components/events/event-setup/event-type-pricing-matrices/event-type-pricing-matrix-details/event-type-pricing-matrix-details.component';
import {
  PrivateEventSelectPaymentAmountComponent
// tslint:disable-next-line:max-line-length
} from './components/events/scheduled-events/private-events/private-event-edit-payment/modals/private-event-select-payment-amount/private-event-select-payment-amount.component';
import {
  PrivateEventRefundExcessModalComponent
// tslint:disable-next-line:max-line-length
} from './components/events/scheduled-events/private-events/private-event-edit-payment/modals/private-event-refund-excess-modal/private-event-refund-excess-modal.component';
import {
  PrivateEventRequestPaymentModalComponent
// tslint:disable-next-line:max-line-length
} from './components/events/scheduled-events/private-events/private-event-edit-payment/modals/private-event-request-payment-modal/private-event-request-payment-modal.component';
import {
  PreviewRequestedPaymentEmailModalComponent
// tslint:disable-next-line:max-line-length
} from './components/events/scheduled-events/private-events/private-event-edit-payment/modals/preview-requested-payment-email-modal/preview-requested-payment-email-modal.component';
import {
  PrivateEventResendRequestedPaymentEmailModalComponent
// tslint:disable-next-line:max-line-length
} from './components/events/scheduled-events/private-events/private-event-edit-payment/modals/private-event-resend-requested-payment-email-modal/private-event-resend-requested-payment-email-modal.component';
import { SelectStaffUserModalComponent } from './components/modals/select-staff-user-modal/select-staff-user-modal.component';
import { EventTypeEmailsComponent } from './components/events/event-setup/event-type-emails/event-type-emails.component';
import {
  InboxUnlinkedEntryPassesComponent
} from './components/inbox/inbox-memberships-and-passes/inbox-unlinked-entry-passes/inbox-unlinked-entry-passes.component';
import {
  ReviewUnlinkedEntryPassCustomerModalComponent
// tslint:disable-next-line:max-line-length
} from './components/inbox/inbox-memberships-and-passes/inbox-unlinked-entry-passes/modals/review-unlinked-entry-pass-customer-modal/review-unlinked-entry-pass-customer-modal.component';
import {
  InboxUnlinkedMembershipsComponent
} from './components/inbox/inbox-memberships-and-passes/inbox-unlinked-memberships/inbox-unlinked-memberships.component';
import {
  ReviewUnlinkedMembershipCustomerModalComponent
// tslint:disable-next-line:max-line-length
} from './components/inbox/inbox-memberships-and-passes/inbox-unlinked-memberships/modals/review-unlinked-membership-customer-modal/review-unlinked-membership-customer-modal.component';
import { BulkCreateBookingComponent } from './components/customers/bulk-create-booking/bulk-create-booking.component';
import { InboxMembershipsAndPassesComponent } from './components/inbox/inbox-memberships-and-passes/inbox-memberships-and-passes.component';
import {
  ReviewMembershipPendingStaffApprovalModalComponent
// tslint:disable-next-line:max-line-length
} from './components/inbox/inbox-memberships-and-passes/inbox-memberships-pending-staff-approval/modals/review-unlinked-membership-customer-modal/review-membership-pending-staff-approval-modal.component';
import {
  InboxMembershipsPendingStaffApprovalComponent
// tslint:disable-next-line:max-line-length
} from './components/inbox/inbox-memberships-and-passes/inbox-memberships-pending-staff-approval/inbox-memberships-pending-staff-approval.component';
import {
  ConfigureRevenueAccountsModalComponent
} from './components/admin/accounting-connections/configure-accounting-connection/modals/configure-revenue-accounts-modal/configure-revenue-accounts-modal.component';
import { LocationSettingsComponent } from './components/admin/location-settings/location-settings.component';
import {
  AddCustomerTagModalComponent
} from './components/customers/customer-detail/modals/add-customer-tag-modal/add-customer-tag-modal.component';
import {
  EditCustomerTagModalComponent
} from './components/customers/customer-detail/modals/edit-customer-tag-modal/edit-customer-tag-modal.component';
import {
  BulkAddOrRemoveCustomerTagModalComponent
} from './components/customers/modals/bulk-add-or-remove-customer-tag-modal/bulk-add-or-remove-customer-tag-modal.component';
import { NgChartsModule } from 'ng2-charts';
import { SetXeroDepositAccountModalComponent } from './components/admin/accounting-connections/configure-accounting-connection/modals/set-xero-deposit-account-modal/set-xero-deposit-account-modal.component';
import { CustomerExportModalComponent } from './components/customers/modals/customer-export-modal/customer-export-modal.component';
import {
  MembershipCancellationReasonSettingsComponent
} from './components/admin/memberships/membership-cancellation-reason-settings/membership-cancellation-reason-settings.component';
import { MembershipFreezeReasonSettingsComponent } from './components/admin/memberships/membership-freeze-reason-settings/membership-freeze-reason-settings.component';
import { ChallengesComponent } from './components/admin/challenges/challenges.component';
import { ChallengeDetailComponent } from './components/admin/challenges/challenge-detail/challenge-detail.component';
import { AddCustomerToChallengeModalComponent } from './components/customers/customer-detail/modals/add-customer-to-challenge-modal/add-customer-to-challenge-modal.component';
import { CustomerChallengeModalComponent } from './components/customers/customer-detail/modals/customer-challenge-modal/customer-challenge-modal.component';
import { BulkAddOrRemoveCustomerChallengeModalComponent } from './components/customers/modals/bulk-add-or-remove-customer-challenge-modal/bulk-add-or-remove-customer-challenge-modal.component';
import { BulkAddCustomersToAssociationModalComponent } from './components/customers/modals/bulk-add-customers-to-association-modal/bulk-add-customers-to-association-modal.component';
import { SetMailchimpAudienceModalComponent } from './components/admin/organization-settings/modals/set-mailchimp-audience-modal/set-mailchimp-audience-modal.component';
import { SetEventPartInstructorsModalComponent } from './components/events/scheduled-events/events-list/modals/set-event-part-instructors-modal/set-event-part-instructors-modal.component';
import { EditEventInstructorsComponent } from './components/events/helpers/edit-event-instructors/edit-event-instructors.component';
import { EntryPassDiscountsComponent } from './components/admin/memberships/entry-pass-discounts/entry-pass-discounts.component';
import { EntryPassDiscountDetailComponent } from './components/admin/memberships/entry-pass-discounts/entry-pass-discount-detail/entry-pass-discount-detail.component';
import { MembershipDiscountsComponent } from './components/admin/memberships/membership-discounts/membership-discounts.component';
import { MembershipDiscountDetailComponent } from './components/admin/memberships/membership-discounts/membership-discount-detail/membership-discount-detail.component';
import {
  EditRecurringCustomerMembershipRecurringDiscountComponent
} from './components/customers/customer-detail/sub-lists/memberships-and-passes-sub-list/modals/edit-recurring-customer-membership-modal/edit-recurring-customer-membership-recurring-discount/edit-recurring-customer-membership-recurring-discount.component';
import {
  EditRecurringCustomerMembershipOneOffDiscountComponent
} from './components/customers/customer-detail/sub-lists/memberships-and-passes-sub-list/modals/edit-recurring-customer-membership-modal/edit-recurring-customer-membership-one-off-discount/edit-recurring-customer-membership-one-off-discount.component';
import { EventDiscountsComponent } from './components/events/event-setup/event-discounts/event-discounts.component';
import { EventDiscountDetailComponent } from './components/events/event-setup/event-discounts/event-discount-detail/event-discount-detail.component';
import { OpenDocumentForCustomerModalComponent } from './components/customers/customer-detail/modals/open-document-for-customer-modal/open-document-for-customer-modal.component';
import { AssociationsComponent } from './components/associations/associations.component';
import { AssociationProfileComponent } from './components/associations/association-profile/association-profile.component';
import { CreateNewAssociationModalComponent } from './components/associations/modals/create-new-association-modal/create-new-association-modal.component';
import { ConfigureAssociationComponent } from './components/associations/association-profile/configure-association/configure-association.component';
import { AddAssociationManagerModalComponent } from './components/associations/association-profile/modals/add-association-manager-modal/add-association-manager-modal.component';
import { EditAssociationManagerModalComponent } from './components/associations/association-profile/modals/edit-association-manager-modal/edit-association-manager-modal.component';
import { AddAssociationMemberModalComponent } from './components/associations/association-profile/modals/add-association-member-modal/add-association-member-modal.component';
import { AssociationMembersSubListComponent } from './components/associations/association-profile/sub-lists/association-members-sub-list/association-members-sub-list.component';
import { AddCustomerToAssociationModalComponent } from './components/customers/customer-detail/modals/add-customer-to-association-modal/add-customer-to-association-modal.component';
import { EditAssociationForCustomerModalComponent } from './components/customers/customer-detail/modals/edit-association-for-customer-modal/edit-association-for-customer-modal.component';
import { AssociationDiscountsSubListComponent } from './components/associations/association-profile/sub-lists/association-discounts-sub-list/association-discounts-sub-list.component';
import { AddAssociationAlertModalComponent } from './components/associations/association-profile/modals/add-association-alert-modal/add-association-alert-modal.component';
import { CalendarEventModalComponent } from './components/events/scheduled-events/event-calendar/modals/calendar-event-modal/calendar-event-modal.component';
import { ScheduledEventsFiltersModalComponent } from './components/events/scheduled-events/events-list/modals/scheduled-events-filters-modal/scheduled-events-filters-modal.component';
import { EditCustomerMailchimpGroupsModalComponent } from './components/customers/customer-detail/edit-customer-profile/modals/edit-customer-mailchimp-groups-modal/edit-customer-mailchimp-groups-modal.component';
import { PreviewEmailOptInModalComponent } from './components/admin/organization-settings/modals/preview-email-opt-in-modal/preview-email-opt-in-modal.component';
import { EditPrepaidCustomerMembershipDaysRemainingComponent } from './components/customers/customer-detail/sub-lists/memberships-and-passes-sub-list/modals/edit-non-recurring-customer-membership-modal/edit-prepaid-customer-membership-days-remaining/edit-prepaid-customer-membership-days-remaining.component';
import { CustomerMedicalNotesModalComponent } from './components/customers/customer-detail/modals/customer-medical-notes-modal/customer-medical-notes-modal.component';
import { AddCustomerMedicalNotesModalComponent } from './components/customers/customer-detail/modals/add-customer-medical-notes-modal/add-customer-medical-notes-modal.component';
import { QuickAddOccurrencesModalComponent } from './components/events/event-setup/event-type-schedules/event-type-edit-schedule/event-type-edit-schedule-non-repeating/modals/quick-add-occurrences-modal/quick-add-occurrences-modal.component';
import { StripeReadersConfigComponent } from './components/admin/payment-processor-connections/configure-payment-processor-connection/stripe-readers-config/stripe-readers-config.component';
import { AddStripeReaderModalComponent } from './components/admin/payment-processor-connections/configure-payment-processor-connection/stripe-readers-config/modals/add-stripe-reader-modal/add-stripe-reader-modal.component';
import { EditStripeReaderModalComponent } from './components/admin/payment-processor-connections/configure-payment-processor-connection/stripe-readers-config/modals/edit-stripe-reader-modal/edit-stripe-reader-modal.component';
import { RemoveStripeReaderModalComponent } from './components/admin/payment-processor-connections/configure-payment-processor-connection/stripe-readers-config/modals/remove-stripe-reader-modal/remove-stripe-reader-modal.component';
import { StripeTerminalServerDrivenPaymentModalComponent } from './components/modals/stripe-terminal-server-driven-payment-modal/stripe-terminal-server-driven-payment-modal.component';
import { SelectPaymentMethodModalComponent } from './components/modals/select-payment-method-modal/select-payment-method-modal.component';
import { LiveReaderRefusalAlertComponent } from './components/modals/live-reader-refusal-alert/live-reader-refusal-alert.component';
import { LiveReaderRefusalModalComponent } from './components/modals/live-reader-refusal-modal/live-reader-refusal-modal.component';
import { SetSquareLocationMapModalComponent } from './components/admin/organization-settings/modals/set-square-location-map-modal/set-square-location-map-modal.component';
import { AssociationActivityLogSubListComponent } from './components/associations/association-profile/sub-lists/association-activity-log-sub-list/association-activity-log-sub-list.component';
import { AddCustomerCreditOrVoucherModalComponent } from './components/customers/customer-detail/sub-lists/credits-and-vouchers-sub-list/modals/add-customer-credit-or-voucher-modal/add-customer-credit-or-voucher-modal.component';
import { EventTypeCategoriesComponent } from './components/events/event-setup/event-type-categories/event-type-categories.component';
import { EventTypeCategoryDetailComponent } from './components/events/event-setup/event-type-categories/event-type-category-detail/event-type-category-detail.component';
import { EditRecurringCustomerMembershipAddOnsNextBillDateComponent } from './components/customers/customer-detail/sub-lists/memberships-and-passes-sub-list/modals/edit-recurring-customer-membership-modal/edit-recurring-customer-membership-add-ons-next-bill-date/edit-recurring-customer-membership-add-ons-next-bill-date.component';
import { EditRecurringCustomerMembershipFixFailedPaymentComponent } from './components/customers/customer-detail/sub-lists/memberships-and-passes-sub-list/modals/edit-recurring-customer-membership-modal/edit-recurring-customer-membership-fix-failed-payment/edit-recurring-customer-membership-fix-failed-payment.component';
import { EditRecurringCustomerMembershipPurchaseUpfrontPaymentOptionComponent } from './components/customers/customer-detail/sub-lists/memberships-and-passes-sub-list/modals/edit-recurring-customer-membership-modal/edit-recurring-customer-membership-purchase-upfront-payment-option/edit-recurring-customer-membership-purchase-upfront-payment-option.component';
import { SetPaymentProcessorFeeAccountModalComponent } from './components/admin/accounting-connections/configure-accounting-connection/modals/set-payment-processor-fee-account-modal/set-payment-processor-fee-account-modal.component';
import { AccountingConnectionsComponent } from './components/admin/accounting-connections/accounting-connections.component';
import { AssignAccountingConnectionModalComponent } from './components/admin/accounting-connections/modals/assign-accounting-connection-modal/assign-accounting-connection-modal.component';
import { AssociationEntryPassesSubListComponent } from './components/associations/association-profile/sub-lists/association-entry-passes-sub-list/association-entry-passes-sub-list.component';
import { PaymentProcessorConnectionsComponent } from './components/admin/payment-processor-connections/payment-processor-connections.component';
import { ConfigurePaymentProcessorConnectionComponent } from './components/admin/payment-processor-connections/configure-payment-processor-connection/configure-payment-processor-connection.component';
import { AssignPaymentProcessorConnectionModalComponent } from './components/admin/payment-processor-connections/modals/assign-payment-processor-connection-modal/assign-payment-processor-connection-modal.component';
import { ReviewAssociationJoinRequestModalComponent } from './components/inbox/inbox-associations/inbox-association-join-requests/modals/review-association-join-request-modal/review-association-join-request-modal.component';
import { InboxAssociationsComponent } from './components/inbox/inbox-associations/inbox-associations.component';
import { InboxAssociationJoinRequestsComponent } from './components/inbox/inbox-associations/inbox-association-join-requests/inbox-association-join-requests.component';
import { EditRecurringCustomerMembershipScheduleUnfreezeComponent } from './components/customers/customer-detail/sub-lists/memberships-and-passes-sub-list/modals/edit-recurring-customer-membership-modal/edit-recurring-customer-membership-schedule-unfreeze/edit-recurring-customer-membership-schedule-unfreeze.component';
import { DuplicateOccurrenceModalComponent } from './components/events/event-setup/event-type-schedules/event-type-schedules-non-repeating/modals/duplicate-occurrence-modal/duplicate-occurrence-modal.component';
import { EventDetailComponent } from './components/events/scheduled-events/events-list/event-detail/event-detail.component';
import { EventParticipantsSubListComponent } from './components/events/scheduled-events/events-list/event-detail/sub-lists/event-participants-sub-list/event-participants-sub-list.component';
import { EventTypeWaitlistConfigComponent } from './components/events/event-setup/event-type-waitlist-config/event-type-waitlist-config.component';
import { EventWaitlistSubListComponent } from './components/events/scheduled-events/events-list/event-detail/sub-lists/event-waitlist-sub-list/event-waitlist-sub-list.component';
import { ExportEventWaitlistModalComponent } from './components/events/modals/export-event-waitlist-modal/export-event-waitlist-modal.component';
import { AddToEventWaitlistModalComponent } from './components/events/scheduled-events/events-list/event-detail/sub-lists/event-waitlist-sub-list/modals/add-to-event-waitlist-modal/add-to-event-waitlist-modal.component';
import { InviteWaitlistEntryToBookModalComponent } from './components/events/scheduled-events/events-list/event-detail/sub-lists/event-waitlist-sub-list/modals/invite-waitlist-entry-to-book-modal/invite-waitlist-entry-to-book-modal.component';
import { DeadlineDateTimeInputComponent } from './components/events/deadline-date-time-input/deadline-date-time-input.component';
import { InviteExpiryInputComponent } from './components/events/invite-expiry-input/invite-expiry-input.component';
import { AgeRestrictionBypassWarningComponent } from './components/events/age-restriction-bypass-warning/age-restriction-bypass-warning.component';
import { ModifyPendingWaitlistEntryModalComponent } from './components/events/scheduled-events/events-list/event-detail/sub-lists/event-waitlist-sub-list/modals/modify-waitlist-entry-booking-deadline-modal/modify-pending-waitlist-entry-modal.component';
import { ResendPendingWaitlistInvitationsModalComponent } from './components/events/scheduled-events/events-list/event-detail/sub-lists/event-waitlist-sub-list/modals/resend-pending-waitlist-invitations-modal/resend-pending-waitlist-invitations-modal.component';
import { CustomerProfileConfigComponent } from './components/admin/customer-profile-config/customer-profile-config.component';
import {
  CustomCustomerFieldsConfigComponent
} from './components/admin/customer-profile-config/custom-customer-fields-config/custom-customer-fields-config.component';
import { AddCustomCustomerFieldModalComponent } from './components/admin/customer-profile-config/custom-customer-fields-config/modals/add-custom-customer-field-modal/add-custom-customer-field-modal.component';
import { EditCustomCustomerFieldModalComponent } from './components/admin/customer-profile-config/custom-customer-fields-config/modals/edit-custom-customer-field-modal/edit-custom-customer-field-modal.component';
import { SetSquareCustomerGroupsManagedByCapitanModalComponent } from './components/admin/organization-settings/modals/set-square-customer-groups-managed-by-capitan-modal/set-square-customer-groups-managed-by-capitan-modal.component';
import { EventNotesSubListComponent } from './components/events/scheduled-events/events-list/event-detail/sub-lists/event-notes-sub-list/event-notes-sub-list.component';
import { AddEventNoteModalComponent } from './components/events/scheduled-events/events-list/event-detail/sub-lists/event-notes-sub-list/modals/add-event-note-modal/add-event-note-modal.component';
import { EditEventNoteModalComponent } from './components/events/scheduled-events/events-list/event-detail/sub-lists/event-notes-sub-list/modals/edit-event-note-modal/edit-event-note-modal.component';
import { TransferCustomerEntryPassModalComponent } from './components/customers/customer-detail/sub-lists/memberships-and-passes-sub-list/modals/transfer-customer-entry-pass-modal/transfer-customer-entry-pass-modal.component';
import { EventTypeScheduledEventsColumnsComponent } from './components/events/event-setup/event-type-scheduled-events-columns/event-type-scheduled-events-columns.component';
import { AddScheduledEventsColumnModalComponent } from './components/events/event-setup/event-type-scheduled-events-columns/modals/add-scheduled-events-column-modal/add-scheduled-events-column-modal.component';
import { EditRecurringCustomerMembershipFreezeIndividualComponent } from './components/customers/customer-detail/sub-lists/memberships-and-passes-sub-list/modals/edit-recurring-customer-membership-modal/edit-recurring-customer-membership-freeze-individual/edit-recurring-customer-membership-freeze-individual.component';
import { EditRecurringCustomerMembershipUndoScheduledFreezeIndividualComponent } from './components/customers/customer-detail/sub-lists/memberships-and-passes-sub-list/modals/edit-recurring-customer-membership-modal/edit-recurring-customer-membership-undo-scheduled-freeze-individual/edit-recurring-customer-membership-undo-scheduled-freeze-individual.component';
import { EditRecurringCustomerMembershipScheduleUnfreezeIndividualComponent } from './components/customers/customer-detail/sub-lists/memberships-and-passes-sub-list/modals/edit-recurring-customer-membership-modal/edit-recurring-customer-membership-schedule-unfreeze-individual/edit-recurring-customer-membership-schedule-unfreeze-individual.component';
import { VoucherPacksComponent } from './components/events/event-setup/voucher-packs/voucher-packs.component';
import {
  VoucherPackDetailComponent
} from './components/events/event-setup/voucher-packs/voucher-pack-detail/voucher-pack-detail.component';
import { UnsavedChangesGuard } from '../../unsaved-changes-guard.service';
import { OverflowMenuDirective } from '../../overflow-menu.directive';
import { PurchaseVoucherPackModalComponent } from './components/customers/customer-detail/sub-lists/credits-and-vouchers-sub-list/modals/purchase-voucher-pack-modal/purchase-voucher-pack-modal.component';
import { StaffSiteCommonModule } from './staff-site-common.module';
import { BrowserModule } from '@angular/platform-browser';
import { CustomersComponent } from './components/customers/customers.component';
import { SelectEventCategoryModalComponent } from './components/modals/select-event-category-modal/select-event-category-modal.component';
import { SelectEventTypeModalComponent } from './components/modals/select-event-type-modal/select-event-type-modal.component';
import { SelectEntryPassModalComponent } from './components/modals/select-entry-pass-modal/select-entry-pass-modal.component';
import { BulkUploadAssociationMemberModalComponent } from './components/associations/association-profile/modals/bulk-upload-association-member-modal/bulk-upload-association-member-modal.component';
import { RefundPaymentModalComponent } from './components/payments/modals/refund-payment-modal/refund-payment-modal.component';
import { ChangePaymentCustomerModalComponent } from './components/payments/modals/change-payment-customer-modal/change-payment-customer-modal.component';
import { SelectLocationModalComponent } from './components/modals/select-location-modal/select-location-modal.component';
import { ClimberAppCustomizationSettingsComponent } from './components/admin/climber-app-customization-settings/climber-app-customization-settings.component';
import { AdminComponent } from './components/admin/admin.component';
import { OrgWideMembershipSettingsComponent } from './components/admin/memberships/org-wide-membership-settings/org-wide-membership-settings.component';
import { EditGroupMembershipMembersImmediatelyComponent } from './components/customers/customer-detail/sub-lists/memberships-and-passes-sub-list/modals/edit-group-membership-members-immediately/edit-group-membership-members-immediately.component';
import { SetLightspeedCustomerGroupsManagedByCapitanModalComponent } from './components/admin/organization-settings/modals/set-lightspeed-customer-groups-managed-by-capitan-modal/set-lightspeed-customer-groups-managed-by-capitan-modal.component';
import { AddGroupMembershipMemberNextBillDateComponent } from './components/customers/customer-detail/sub-lists/memberships-and-passes-sub-list/modals/edit-recurring-customer-membership-modal/add-group-membership-member-next-bill-date/add-group-membership-member-next-bill-date.component';
import { RemoveGroupMembershipMemberNextBillDateComponent } from './components/customers/customer-detail/sub-lists/memberships-and-passes-sub-list/modals/edit-recurring-customer-membership-modal/remove-group-membership-member-next-bill-date/remove-group-membership-member-next-bill-date.component';
import { BulkUploadEventParticipantsModalComponent } from './components/events/scheduled-events/events-list/event-detail/sub-lists/event-participants-sub-list/modals/bulk-upload-event-participants-modal/bulk-upload-event-participants-modal.component';
import { ManageDocumentModalComponent } from './components/customers/customer-detail/modals/manage-document-modal/manage-document-modal.component';
import { RemoveAssociationModalComponent } from './components/associations/association-profile/modals/remove-association-modal/remove-association-modal.component';
import { MembershipCancellationDiscountOfferPreviewModalComponent } from './components/admin/memberships/membership-discounts/membership-discount-detail/modals/membership-cancellation-discount-offer-preview-modal/membership-cancellation-discount-offer-preview-modal.component';
import { EditRecurringCustomerMembershipChangePaymentMethodComponent } from './components/customers/customer-detail/sub-lists/memberships-and-passes-sub-list/modals/edit-recurring-customer-membership-modal/edit-recurring-customer-membership-change-payment-method/edit-recurring-customer-membership-change-payment-method.component';
import {
  EditRecurringCustomerMembershipAddPaymentMethodComponent
// tslint:disable-next-line:max-line-length
} from './components/customers/customer-detail/sub-lists/memberships-and-passes-sub-list/modals/edit-recurring-customer-membership-modal/edit-recurring-customer-membership-add-payment-method/edit-recurring-customer-membership-add-payment-method.component';
import {
  SetLightspeedLocationMapModalComponent
} from './components/admin/organization-settings/modals/set-lightspeed-location-map-modal/set-lightspeed-location-map-modal.component';
import {
  CreateGuestPassModalComponent
} from './components/customers/customer-detail/sub-lists/memberships-and-passes-sub-list/modals/create-guest-pass-modal/create-guest-pass-modal.component';
import {
  ManageGuestPassModalComponent
} from './components/customers/customer-detail/sub-lists/memberships-and-passes-sub-list/modals/manage-guest-pass-modal/manage-guest-pass-modal.component';
import {
  BulkCreateGuestPassesModalComponent
} from './components/customers/modals/bulk-create-guest-passes-modal/bulk-create-guest-passes-modal.component';
import { PaymentDetailsModalComponent } from './components/payments/modals/payment-details-modal/payment-details-modal.component';
import { GiftingConfigComponent } from './components/admin/gifting-config/gifting-config.component';
import {
  MembershipGiftingConfigDetailComponent
} from './components/admin/gifting-config/membership-gifting-config-detail/membership-gifting-config-detail.component';
import {
  EntryPassGiftingConfigDetailComponent
} from './components/admin/gifting-config/entry-pass-gifting-config-detail/entry-pass-gifting-config-detail.component';
import {
  ChangeGiftExpirationDateModalComponent
} from './modules/reporting/components/gifts/modals/change-gift-expiration-date-modal/change-gift-expiration-date-modal.component';
import {
  ExpireGiftImmediatelyModalComponent
} from './modules/reporting/components/gifts/modals/expire-gift-immediately-modal/expire-gift-immediately-modal.component';
import {
  AddDateRangeOptionModalComponent
} from './components/admin/memberships/membership-detail/modals/add-date-range-option-modal/add-date-range-option-modal.component';
import {
  VoucherPackGiftingConfigDetailComponent
} from './components/admin/gifting-config/voucher-pack-gifting-config-detail/voucher-pack-gifting-config-detail.component';
import { GiftDiscountsComponent } from './components/admin/gifting-config/gift-discounts/gift-discounts.component';
import {
  MembershipGiftDiscountDetailComponent
} from './components/admin/gifting-config/gift-discounts/membership-gift-discount-detail/membership-gift-discount-detail.component';
import {
  EntryPassGiftDiscountDetailComponent
} from './components/admin/gifting-config/gift-discounts/entry-pass-gift-discount-detail/entry-pass-gift-discount-detail.component';
import {
  VoucherPackGiftDiscountDetailComponent
} from './components/admin/gifting-config/gift-discounts/voucher-pack-gift-discount-detail/voucher-pack-gift-discount-detail.component';
import {
  ObtainBulkOperationCustomersModalComponent
} from './components/customers/modals/obtain-bulk-operation-customers-modal/obtain-bulk-operation-customers-modal.component';
import {
  AddEventTypeParticipantAgreementComponent
} from './components/events/event-setup/event-type-edit/modals/add-event-type-participant-agreement/add-event-type-participant-agreement.component';
import { EventTypeWaitlistsComponent } from './components/events/scheduled-events/event-type-waitlists/event-type-waitlists.component';
import {
  EventTypeWaitlistEntriesComponent
} from './components/events/scheduled-events/event-type-waitlists/event-type-waitlist-entries/event-type-waitlist-entries.component';
import { WaitlistsComponent } from './components/events/scheduled-events/waitlists/waitlists.component';
import { EventWaitlistsComponent } from './components/events/scheduled-events/waitlists/event-waitlists/event-waitlists.component';
import {
  EventWaitlistsFiltersModalComponent
} from './components/events/scheduled-events/waitlists/event-waitlists/modals/event-waitlists-filters-modal/event-waitlists-filters-modal.component';
import { RostersComponent } from './components/events/scheduled-events/rosters/rosters.component';
import {
  InviteParticipantsToAnotherEventModalComponent
} from './components/events/scheduled-events/events-list/event-detail/sub-lists/event-participants-sub-list/modals/invite-participants-to-another-event-modal/invite-participants-to-another-event-modal.component';
import {
  InviteToEventModalComponent
} from './components/events/scheduled-events/events-list/event-detail/sub-lists/event-participants-sub-list/modals/invite-to-event-modal/invite-to-event-modal.component';
import { EventTypeRostersComponent } from './components/events/event-setup/event-type-rosters/event-type-rosters.component';
import {
  EventTypeRosterEditComponent
} from './components/events/event-setup/event-type-rosters/event-type-roster-edit/event-type-roster-edit.component';
import {
  EventTypePricingMembershipComponent
} from './components/events/event-setup/event-type-pricing-membership/event-type-pricing-membership.component';
import { AddToRosterModalComponent } from './components/events/modals/add-to-roster-modal/add-to-roster-modal.component';
import {
  InboxPotentialDuplicateCustomersComponent
} from './components/inbox/inbox-customers/inbox-potential-duplicate-customers/inbox-potential-duplicate-customers.component';
import {
  ViewPotentialDuplicateCustomersModalComponent
} from './components/inbox/inbox-customers/inbox-potential-duplicate-customers/modals/view-potential-duplicate-customer-modal/view-potential-duplicate-customer-modal.component';
import {
  InviteWaitlistEntryToRosterModalComponent
} from './components/events/scheduled-events/events-list/event-detail/sub-lists/event-waitlist-sub-list/modals/invite-waitlist-entry-to-roster-modal/invite-waitlist-entry-to-roster-modal.component';
import { InviteToRosterModalComponent } from './components/events/modals/invite-to-roster-modal/invite-to-roster-modal.component';
import {
  RosterInviteFirstBillComponent
} from './components/events/modals/roster-invite-first-bill/roster-invite-first-bill.component';
import { RedemptionCodesComponent } from './components/admin/redemption-codes/redemption-codes.component';
import { RedemptionCodeSetComponent } from './components/admin/redemption-codes/redemption-code-set/redemption-code-set.component';
import {
  CreateRedemptionCodeSetModalComponent
} from './components/admin/redemption-codes/modals/create-redemption-code-set-modal/create-redemption-code-set-modal.component';
import {
  RenameRedemptionCodeSetModalComponent
} from './components/admin/redemption-codes/redemption-code-set/modals/rename-redemption-code-set-modal/rename-redemption-code-set-modal.component';
import {
  EditRedemptionCodeSetExpirationModalComponent
} from './components/admin/redemption-codes/redemption-code-set/modals/edit-redemption-code-set-expiration-modal/edit-redemption-code-set-expiration-modal.component';
import {
  ExportRedemptionCodesModalComponent
} from './components/admin/redemption-codes/redemption-code-set/modals/export-redemption-codes-modal/export-redemption-codes-modal.component';
import { CustomInboxConfigComponent } from './components/admin/custom-inbox-config/custom-inbox-config.component';
import { CustomInboxEditComponent } from './components/admin/custom-inbox-config/custom-inbox-edit/custom-inbox-edit.component';
import {
  CustomInboxConditionModalComponent
// tslint:disable-next-line:max-line-length
} from './components/admin/custom-inbox-config/custom-inbox-edit/modals/custom-inbox-condition-modal/custom-inbox-condition-modal.component';
import { InboxCustomComponent } from './components/inbox/inbox-custom/inbox-custom.component';
import {
  ViewCustomInboxItemComponent
} from './components/inbox/inbox-custom/modals/view-custom-inbox-item/view-custom-inbox-item.component';
import {
  CustomInboxItemFiltersModalComponent
  // tslint:disable-next-line:max-line-length
} from './components/inbox/inbox-custom/modals/custom-inbox-item-filters-modal/custom-inbox-item-filters-modal.component';
import { QRCodeModule } from 'angularx-qrcode';
import {
  SetUpTwoFactorAuthenticationModalComponent
} from './components/account-settings/modals/set-up-two-factor-authentication-modal/set-up-two-factor-authentication-modal.component';
import {
  ScheduledEventsDefaultRedirectComponent
} from './components/events/scheduled-events/scheduled-events-default-redirect/scheduled-events-default-redirect.component';
import { ClipboardModule } from '@angular/cdk/clipboard';
import {
  ChangeCustomerMembershipRosterComponent
} from './components/customers/customer-detail/sub-lists/memberships-and-passes-sub-list/modals/edit-recurring-customer-membership-modal/change-customer-membership-roster/change-customer-membership-roster.component';
import {
  DuplicateEventTypeModalComponent
} from './components/events/event-setup/event-types-list/modals/duplicate-event-type-modal/duplicate-event-type-modal.component';
import {
  EditMembershipPerLocationPricingMatrixModalComponent
} from './components/admin/memberships/membership-detail/modals/edit-membership-per-location-pricing-matrix-modal/edit-membership-per-location-pricing-matrix-modal.component';
import {
  AddMembershipUpfrontPaymentOptionModalComponent
} from './components/admin/memberships/membership-detail/modals/add-membership-upfront-payment-option-modal/add-membership-upfront-payment-option-modal.component';
import {
  AddMxMerchantConnectionModalComponent
// tslint:disable-next-line:max-line-length
} from './components/admin/payment-processor-connections/modals/add-mx-merchant-connection-modal/add-mx-merchant-connection-modal.component';
import {
  EditEntryPassPerLocationPricingMatrixModalComponent
} from './components/admin/memberships/entry-pass-detail/modals/edit-entry-pass-per-location-pricing-matrix-modal/edit-entry-pass-per-location-pricing-matrix-modal.component';
import {
  AddMxMerchantTerminalModalComponent
} from './components/admin/payment-processor-connections/configure-payment-processor-connection/stripe-readers-config/modals/add-mx-merchant-terminal-modal/add-mx-merchant-terminal-modal.component';
import {
  TakeCustomerWebcamPhotoModalComponent
} from './components/customers/customer-detail/edit-customer-profile/modals/take-customer-webcam-photo-modal/take-customer-webcam-photo-modal.component';
import { WebcamModule } from 'ngx-webcam';
import { GiftPrintOptionsComponent } from './components/admin/gifting-config/gift-print-options/gift-print-options.component';
import {
  GiftCenterHomePageComponent
} from './components/admin/gifting-config/gift-center-home-page/gift-center-home-page.component';
import {
  ConfigureGiftCenterCarouselImageModalComponent
} from './components/admin/gifting-config/gift-center-home-page/modals/configure-gift-center-carousel-image-modal/configure-gift-center-carousel-image-modal.component';
import {
  GiftCenterHomePreviewModalComponent
} from './components/admin/gifting-config/gift-center-home-page/modals/gift-center-home-preview-modal/gift-center-home-preview-modal.component';
import {
  GiftPdfPreviewModalComponent
} from './components/admin/gifting-config/gift-print-options/modals/gift-pdf-preview-modal/gift-pdf-preview-modal.component';
import {
  ConfigureGiftPrintBannerImageOptionModalComponent
} from './components/admin/gifting-config/gift-print-options/modals/configure-gift-print-banner-image-option-modal/configure-gift-print-banner-image-option-modal.component';
import {
  GiftCardSalesConfigComponent
} from './components/admin/gifting-config/gift-card-sales-config/gift-card-sales-config.component';
import {
  GiftCardRecipientTextPreviewModalComponent
} from './components/admin/gifting-config/gift-card-sales-config/modals/gift-card-recipient-text-preview-modal/gift-card-recipient-text-preview-modal.component';
import {
  PreviewInviteEmailModalComponent
} from './components/admin/organization-settings/modals/preview-invite-email-modal/preview-invite-email-modal.component';
import {
  CustomerProfilePreviewModalComponent
} from './components/admin/customer-profile-config/modals/customer-profile-preview-modal/customer-profile-preview-modal.component';
import {
  PreviewWaitlistConfirmationEmailModalComponent
} from './components/events/event-setup/event-type-waitlist-config/modals/preview-waitlist-confirmation-email-modal/preview-waitlist-confirmation-email-modal.component';
import {
  PreviewDocumentEmailModalComponent
} from './components/admin/document-templates/document-template-edit/modals/preview-document-email-modal/preview-document-email-modal.component';
import { SaleInitComponent } from './components/sale/sale-init/sale-init.component';
import {
  AddEntryPassLineItemToSaleModalComponent
} from './components/sale/sale-init/modals/add-entry-pass-line-item-to-sale-modal/add-entry-pass-line-item-to-sale-modal.component';
import {
  AddVoucherPackLineItemToSaleModalComponent
} from './components/sale/sale-init/modals/add-voucher-pack-line-item-to-sale-modal/add-voucher-pack-line-item-to-sale-modal.component';
import {
  AddMembershipLineItemToSaleModalComponent
} from './components/sale/sale-init/modals/add-membership-line-item-to-sale-modal/add-membership-line-item-to-sale-modal.component';
import {
  SelectRegisterModalComponent
} from './components/sale/sale-init/modals/select-register-modal/select-register-modal.component';
import {
  RecentlyActiveCustomersComponent
} from './components/recently-active-customers/recently-active-customers.component';
import {
  SetCapitanLightspeedUserModalComponent
} from './components/admin/organization-settings/modals/set-capitan-lightspeed-user-modal/set-capitan-lightspeed-user-modal.component';
import {
  SetUserLightspeedUserModalComponent
} from './components/admin/user-list/user/modals/set-user-lightspeed-user-modal/set-user-lightspeed-user-modal.component';
import {
  BulkAddCustomerAlertModalComponent
} from './components/customers/modals/bulk-add-customer-alert-modal/bulk-add-customer-alert-modal.component';
import {
  BulkSendAllthenticateInviteModalComponent
} from './components/customers/modals/bulk-send-allthenticate-invite-modal/bulk-send-allthenticate-invite-modal.component';
import {
  MembershipCategoriesComponent
} from './components/admin/memberships/membership-categories/membership-categories.component';
import {
  MembershipCategoryDetailComponent
} from './components/admin/memberships/membership-categories/membership-category-detail/membership-category-detail.component';
import { PaginationRowComponent } from '../../components/pagination-row/pagination-row.component';
import {
  AddEventTypeRequiredWaiverComponent
} from './components/events/event-setup/event-type-edit/modals/add-event-type-required-waiver/add-event-type-required-waiver.component';
import { SelectColumnsModalComponent } from './components/modals/select-columns-modal/select-columns-modal.component';


FullCalendarModule.registerPlugins([
  dayGridPlugin,
  timeGridPlugin,
  listPlugin,
  scrollGridPlugin,
  bootstrapPlugin
]);


@NgModule({
    declarations: [
        StaffSiteComponent,
        NavigationTopComponent,
        NavigationSideComponent,
        CustomersComponent,
        CustomerFiltersModalComponent,
        CreateNewCustomerModalComponent,
        BulkAddOrRemoveCustomerTagModalComponent,
        AccountSettingsComponent,
        CustomerDetailComponent,
        UserListComponent,
        UserComponent,
        EventsComponent,
        EventsListComponent,
        EventCalendarComponent,
        EventTypesListComponent,
        ScheduledEventsComponent,
        EventSetupComponent,
        EventSetupNavComponent,
        EventTypeEditComponent,
        EventTypeSchedulesComponent,
        EventTypeSchedulesRepeatingComponent,
        EventTypeSchedulesNonRepeatingComponent,
        ScheduleOrphanedEventsModalComponent,
        EventTypeEditScheduleComponent,
        EventTypeEditScheduleRepeatingComponent,
        EventTypeEditScheduleNonRepeatingComponent,
        EventTypeScheduleExceptionsComponent,
        EventTypeEditScheduleExceptionComponent,
        EventTypeQuestionsComponent,
        EventTypeScheduledEventsColumnsComponent,
        EventTypePricingMatricesComponent,
        EventTypePricingMatrixDetailsComponent,
        EventTypePricingMatrixEditComponent,
        EditEventTypePricingMatrixFeeModalComponent,
        RemoveEventTypePricingMatrixModalComponent,
        EventSettingsComponent,
        EventTypeCancellationPolicyComponent,
        EventTypeWaitlistConfigComponent,
        EventTypeEmailsComponent,
        CancelEventModalComponent,
        RebookModalComponent,
        ParticipantDetailsModalComponent,
        BookingDetailsModalComponent,
        CreateBookingModalComponent,
        AddParticipantToPrivateEventModalComponent,
        AddToEventWaitlistModalComponent,
        ExportEventAttendeesModalComponent,
        ExportEventWaitlistModalComponent,
        PrivateEventSetupNavComponent,
        PrivateEventEditEventComponent,
        PrivateEventEditReservationsComponent,
        PrivateEventEditPaymentComponent,
        PrivateEventSelectPaymentAmountComponent,
        PrivateEventRefundExcessModalComponent,
        PrivateEventRequestPaymentModalComponent,
        PreviewRequestedPaymentEmailModalComponent,
        PrivateEventResendRequestedPaymentEmailModalComponent,
        CreditsAndVouchersSubListComponent,
        ManageBookingCancellationAndRefundComponent,
        BulkAddCreditOrVoucherComponent,
        BulkCreateBookingComponent,
        DocumentTemplatesComponent,
        DocumentTemplateEditComponent,
        DocumentTemplateElementComponent,
        DocumentTemplateEditSectionsComponent,
        DocumentTemplateEditSettingsComponent,
        DocumentTemplateEditLinkedProductsComponent,
        DocumentTemplateAddComponent,
        DocumentPoliciesComponent,
        DocumentTemplatesNavComponent,
        DocumentPolicyFormComponent,
        DocumentPolicyAddComponent,
        DocumentPolicyEditComponent,
        CustomerProfileLayoutComponent,
        EditCustomerProfileComponent,
        AddLinkedAccountModalComponent,
        LinkingAccountsConfigComponent,
        ReviewPendingLinkedAccountModalComponent,
        ReviewInvalidDocumentModalComponent,
        ViewDismissedInvalidDocumentModalComponent,
        InboxComponent,
        InboxLinkingAccountRequestsComponent,
        InboxInvalidDocumentsComponent,
        InboxDocumentsPendingStaffApprovalComponent,
        ReviewDocumentPendingStaffApprovalModalComponent,
        ViewDismissedDocumentPendingStaffApprovalModalComponent,
        InboxCustomersComponent,
        InboxMembershipsAndPassesComponent,
        ConfirmRemoveLinkedAccountModalComponent,
        InboxUnlinkedMembershipsComponent,
        InboxUnlinkedEntryPassesComponent,
        InboxMembershipsPendingStaffApprovalComponent,
        ReviewUnlinkedMembershipCustomerModalComponent,
        ReviewUnlinkedEntryPassCustomerModalComponent,
        ReviewMembershipPendingStaffApprovalModalComponent,
        TaxRatesConfigComponent,
        AddTaxRateModalComponent,
        EditTaxRateModalComponent,
        RemoveTaxRateModalComponent,
        MembershipsComponent,
        MembershipDetailComponent,
        EditMembershipPerCustomerAddOnModalComponent,
        MembershipPricingSectionComponent,
        EditMembershipPerLocationPricingMatrixModalComponent,
        AddMembershipUpfrontPaymentOptionModalComponent,
        DashboardComponent,
        AddStaffNoteModalComponent,
        EditStaffNoteModalComponent,
        EntryPassDetailComponent,
        EditEntryPassPerLocationPricingMatrixModalComponent,
        MembershipsAndPassesSubListComponent,
        CheckInsSubListComponent,
        CheckInAlertsModalComponent,
        CheckInNoEntryMethodModalComponent,
        CheckInDetailModalComponent,
        CheckInComponent,
        OrganizationSettingsComponent,
        LocationSettingsComponent,
        ConfigureAccountingConnectionComponent,
        SetQuickbooksDepositAccountModalComponent,
        SetXeroDepositAccountModalComponent,
        ConfigureRevenueAccountsModalComponent,
        PaymentsSubListComponent,
        PurchasesSubListComponent,
        RefundPaymentModalComponent,
        ChangePaymentCustomerModalComponent,
        ReservationsSubListComponent,
        UpcomingReservationsSubListComponent,
        CustomerBookingsSubListComponent,
        CustomerActivityLogSubListComponent,
        CustomerEmailsSubListComponent,
        BackendEmailDetailModalComponent,
        PaymentsComponent,
        PaymentFiltersModalComponent,
        ProficienciesConfigComponent,
        AddProficiencyModalComponent,
        EditProficiencyModalComponent,
        RemoveProficiencyModalComponent,
        TimeTrackingConfigComponent,
        AddTimeTrackingRoleModalComponent,
        EditTimeTrackingRoleModalComponent,
        RemoveTimeTrackingRoleModalComponent,
        AddEditCustomerProficiencyModalComponent,
        CustomerEmergencyContactModalComponent,
        CustomerNoteModalComponent,
        CustomerMedicalNotesModalComponent,
        AddCustomerNoteModalComponent,
        AddCustomerMedicalNotesModalComponent,
        AddCustomerAlertModalComponent,
        AddCustomerTagModalComponent,
        EditCustomerTagModalComponent,
        CheckInSoundsConfigComponent,
        ChooseCheckInSharedPassModalComponent,
        ChooseCheckInGuestPassModalComponent,
        EditRecurringCustomerMembershipModalComponent,
        MembershipDetailsModalComponent,
        EditRecurringCustomerMembershipCancelImmediatelyComponent,
        EditRecurringCustomerMembershipCancelFutureBillDateComponent,
        EditRecurringCustomerMembershipUndoCancelFutureBillDateComponent,
        EditRecurringCustomerMembershipFreezeComponent,
        EditRecurringCustomerMembershipFreezeIndividualComponent,
        EditRecurringCustomerMembershipUndoScheduledFreezeComponent,
        EditRecurringCustomerMembershipUndoScheduledFreezeIndividualComponent,
        EditRecurringCustomerMembershipUnfreezeComponent,
        EditRecurringCustomerMembershipRecurringDiscountComponent,
        EditRecurringCustomerMembershipOneOffDiscountComponent,
        EditNonRecurringCustomerMembershipModalComponent,
        EditNonRecurringCustomerMembershipCancelComponent,
        EditPrepaidCustomerMembershipUnfreezeComponent,
        EditPrepaidCustomerMembershipFreezeImmediatelyComponent,
        EditCustomerMembershipStartDateComponent,
        EditCustomerMembershipBillDateComponent,
        CancelUnactivatedCustomerMembershipComponent,
        EditCustomerMembershipAddOnsImmediatelyComponent,
        EditRecurringCustomerMembershipAddOnsNextBillDateComponent,
        ReassignCustomerMembershipComponent,
        EditCustomerEntryPassModalComponent,
        LinkCustomerEntryPassModalComponent,
        CreateCustomerEntryPassModalComponent,
        PurchaseCustomerEntryPassModalComponent,
        PurchaseCustomerMembershipModalComponent,
        EditActivityLogEntryNotesModalComponent,
        ReviewUnlinkedReservationModalComponent,
        MergeCustomersComponent,
        LocationAreasConfigComponent,
        AddLocationAreaModalComponent,
        EditLocationAreaModalComponent,
        RemoveLocationAreaModalComponent,
        CustomerExportModalComponent,
        ClockInOutModalComponent,
        SelectCustomerModalComponent,
        SelectStaffUserModalComponent,
        MembershipCancellationReasonSettingsComponent,
        MembershipFreezeReasonSettingsComponent,
        OrgWideMembershipSettingsComponent,
        ChallengesComponent,
        ChallengeDetailComponent,
        AddCustomerToChallengeModalComponent,
        CustomerChallengeModalComponent,
        BulkAddOrRemoveCustomerChallengeModalComponent,
        BulkAddCustomersToAssociationModalComponent,
        SetMailchimpAudienceModalComponent,
        SetEventPartInstructorsModalComponent,
        EditEventInstructorsComponent,
        EntryPassDiscountsComponent,
        EntryPassDiscountDetailComponent,
        MembershipDiscountsComponent,
        MembershipDiscountDetailComponent,
        EventDiscountsComponent,
        EventDiscountDetailComponent,
        EventTypeCategoriesComponent,
        EventTypeCategoryDetailComponent,
        VoucherPacksComponent,
        VoucherPackDetailComponent,
        OpenDocumentForCustomerModalComponent,
        AssociationsComponent,
        AssociationProfileComponent,
        CreateNewAssociationModalComponent,
        ConfigureAssociationComponent,
        AddAssociationManagerModalComponent,
        EditAssociationManagerModalComponent,
        AddAssociationMemberModalComponent,
        BulkUploadAssociationMemberModalComponent,
        AssociationMembersSubListComponent,
        AddCustomerToAssociationModalComponent,
        EditAssociationForCustomerModalComponent,
        AssociationDiscountsSubListComponent,
        AddAssociationAlertModalComponent,
        CalendarEventModalComponent,
        ScheduledEventsFiltersModalComponent,
        EditCustomerMailchimpGroupsModalComponent,
        PreviewEmailOptInModalComponent,
        EditPrepaidCustomerMembershipDaysRemainingComponent,
        QuickAddOccurrencesModalComponent,
        StripeReadersConfigComponent,
        AddStripeReaderModalComponent,
        AddMxMerchantTerminalModalComponent,
        EditStripeReaderModalComponent,
        RemoveStripeReaderModalComponent,
        StripeTerminalServerDrivenPaymentModalComponent,
        SelectPaymentMethodModalComponent,
        LiveReaderRefusalAlertComponent,
        LiveReaderRefusalModalComponent,
        SetSquareLocationMapModalComponent,
        SetLightspeedLocationMapModalComponent,
        AssociationActivityLogSubListComponent,
        AddCustomerCreditOrVoucherModalComponent,
        EditRecurringCustomerMembershipFixFailedPaymentComponent,
        EditRecurringCustomerMembershipPurchaseUpfrontPaymentOptionComponent,
        EditRecurringCustomerMembershipChangePaymentMethodComponent,
        EditRecurringCustomerMembershipAddPaymentMethodComponent,
        SetPaymentProcessorFeeAccountModalComponent,
        AccountingConnectionsComponent,
        AssignAccountingConnectionModalComponent,
        AssociationEntryPassesSubListComponent,
        PaymentProcessorConnectionsComponent,
        ConfigurePaymentProcessorConnectionComponent,
        AssignPaymentProcessorConnectionModalComponent,
        AddMxMerchantConnectionModalComponent,
        ReviewAssociationJoinRequestModalComponent,
        InboxAssociationsComponent,
        InboxAssociationJoinRequestsComponent,
        EditRecurringCustomerMembershipScheduleUnfreezeComponent,
        EditRecurringCustomerMembershipScheduleUnfreezeIndividualComponent,
        DuplicateOccurrenceModalComponent,
        EventDetailComponent,
        EventParticipantsSubListComponent,
        EventWaitlistSubListComponent,
        EditGroupMembershipMembersImmediatelyComponent,
        AddGroupMembershipMemberNextBillDateComponent,
        RemoveGroupMembershipMemberNextBillDateComponent,
        InviteWaitlistEntryToBookModalComponent,
        InviteWaitlistEntryToRosterModalComponent,
        ModifyPendingWaitlistEntryModalComponent,
        ResendPendingWaitlistInvitationsModalComponent,
        DeadlineDateTimeInputComponent,
        InviteExpiryInputComponent,
        AgeRestrictionBypassWarningComponent,
        CustomerProfileConfigComponent,
        CustomCustomerFieldsConfigComponent,
        AddCustomCustomerFieldModalComponent,
        EditCustomCustomerFieldModalComponent,
        SetSquareCustomerGroupsManagedByCapitanModalComponent,
        SetLightspeedCustomerGroupsManagedByCapitanModalComponent,
        SetCapitanLightspeedUserModalComponent,
        EventNotesSubListComponent,
        AddEventNoteModalComponent,
        EditEventNoteModalComponent,
        TransferCustomerEntryPassModalComponent,
        AddScheduledEventsColumnModalComponent,
        PurchaseVoucherPackModalComponent,
        SelectEventCategoryModalComponent,
        SelectEventTypeModalComponent,
        SelectEntryPassModalComponent,
        SelectLocationModalComponent,
        BulkUploadEventParticipantsModalComponent,
        ClimberAppCustomizationSettingsComponent,
        ManageDocumentModalComponent,
        AdminComponent,
        RemoveAssociationModalComponent,
        MembershipCancellationDiscountOfferPreviewModalComponent,
        CreateGuestPassModalComponent,
        ManageGuestPassModalComponent,
        BulkCreateGuestPassesModalComponent,
        PaymentDetailsModalComponent,
        GiftingConfigComponent,
        MembershipGiftingConfigDetailComponent,
        EntryPassGiftingConfigDetailComponent,
        VoucherPackGiftingConfigDetailComponent,
        ChangeGiftExpirationDateModalComponent,
        ExpireGiftImmediatelyModalComponent,
        AddDateRangeOptionModalComponent,
        GiftDiscountsComponent,
        MembershipGiftDiscountDetailComponent,
        EntryPassGiftDiscountDetailComponent,
        VoucherPackGiftDiscountDetailComponent,
        ObtainBulkOperationCustomersModalComponent,
        AddEventTypeParticipantAgreementComponent,
        AddEventTypeRequiredWaiverComponent,
        EventTypeWaitlistsComponent,
        EventTypeWaitlistEntriesComponent,
        WaitlistsComponent,
        EventWaitlistsComponent,
        EventWaitlistsFiltersModalComponent,
        RostersComponent,
        InviteParticipantsToAnotherEventModalComponent,
        InviteToEventModalComponent,
        EventTypeRostersComponent,
        EventTypeRosterEditComponent,
        EventTypePricingMembershipComponent,
        AddToRosterModalComponent,
        InboxPotentialDuplicateCustomersComponent,
        ViewPotentialDuplicateCustomersModalComponent,
        InviteToRosterModalComponent,
        RosterInviteFirstBillComponent,
        RedemptionCodesComponent,
        RedemptionCodeSetComponent,
        CreateRedemptionCodeSetModalComponent,
        RenameRedemptionCodeSetModalComponent,
        EditRedemptionCodeSetExpirationModalComponent,
        ExportRedemptionCodesModalComponent,
        CustomInboxConfigComponent,
        CustomInboxEditComponent,
        CustomInboxConditionModalComponent,
        InboxCustomComponent,
        ViewCustomInboxItemComponent,
        CustomInboxItemFiltersModalComponent,
        SetUpTwoFactorAuthenticationModalComponent,
        ScheduledEventsDefaultRedirectComponent,
        ChangeCustomerMembershipRosterComponent,
        DuplicateEventTypeModalComponent,
        TakeCustomerWebcamPhotoModalComponent,
        GiftPrintOptionsComponent,
        GiftCenterHomePageComponent,
        ConfigureGiftCenterCarouselImageModalComponent,
        GiftCenterHomePreviewModalComponent,
        GiftCardSalesConfigComponent,
        ConfigureGiftPrintBannerImageOptionModalComponent,
        GiftPdfPreviewModalComponent,
        GiftCardRecipientTextPreviewModalComponent,
        PreviewInviteEmailModalComponent,
        CustomerProfilePreviewModalComponent,
        PreviewWaitlistConfirmationEmailModalComponent,
        PreviewDocumentEmailModalComponent,
        SaleInitComponent,
        AddEntryPassLineItemToSaleModalComponent,
        AddVoucherPackLineItemToSaleModalComponent,
        AddMembershipLineItemToSaleModalComponent,
        SelectRegisterModalComponent,
        RecentlyActiveCustomersComponent,
        SetUserLightspeedUserModalComponent,
        BulkAddCustomerAlertModalComponent,
        BulkSendAllthenticateInviteModalComponent,
        MembershipCategoriesComponent,
        MembershipCategoryDetailComponent,
        SelectColumnsModalComponent,
        PaginationRowComponent,
        OverflowMenuDirective
    ],
    imports: [
        BrowserModule,
        HttpClientModule,
        FormsModule,
        BrowserAnimationsModule,
        DragDropModule,
        ClipboardModule,
        NgbDatepickerModule,
        NgbDropdownModule,
        NgbTooltipModule,
        NgbTypeaheadModule,
        NgbModalModule,
        NgChartsModule,
        FullCalendarModule,
        QRCodeModule,
        WebcamModule,
        CapitanCommonModule,
        StaffSiteCommonModule,
        StaffSiteRoutingModule,
    ],
    providers: [
        {
            provide: ErrorHandler,
            useValue: Sentry.createErrorHandler({
                showDialog: false,
            }),
        },
        UnsavedChangesGuard,
        // Make stacked (nested) modals layer deterministically (PRO-1219).
        { provide: NgbModal, useClass: StackingNgbModal },
    ],
    bootstrap: [StaffSiteComponent]
})
export class StaffSiteModule { }
